import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Radio,
  Select,
  Space,
  Switch,
} from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import { RevxSpin } from 'components/RevxSpin';
import {
  DaysOfWeekOptions,
  GenericSchedule,
  RoundUpEnum,
} from 'domains/tfDomain';
import { clone, isEmpty, merge } from 'lodash-es';
import moment, { Moment } from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_DRAWER_WIDTH,
  PLATFORM_FORM_ACTION_LAYOUT,
  PLATFORM_FORM_ITEM_LAYOUT,
} from 'utils/constants';
import { RULE_TEXT_LEN_255 } from 'utils/validators';

const TfItemForm = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const saving = useAppSelector(
    (state) => state.loading.effects.tf.saveSchedule
  );
  const item = useAppSelector((state) => state.tf.curItem);
  const onClose = () => {
    dispatch.tf.setState({ curItem: undefined });
  };
  const [isOngoing, setIsOngoing] = useState<boolean>(item!.ongoing!);
  const onFinish = async (values: any) => {
    let updated: GenericSchedule = merge({}, item, values);
    updated.daysOfWeek = clone(values.daysOfWeek);
    if (isOngoing) {
      updated.ongoing = true;
      updated.startDate = undefined;
      updated.endDate = undefined;
    } else {
      updated.ongoing = false;
      updated.startDate = (values.dateRange[0] as Moment).format('YYYY-MM-DD');
      updated.endDate = (values.dateRange[1] as Moment).format('YYYY-MM-DD');
    }
    await dispatch.tf.saveSchedule(updated);
    onClose();
  };
  const onRemove = async () => {
    if (item?.id) {
      await dispatch.tf.removeSchedule(item);
      onClose();
    }
  };

  const actionBar = (
    <Form.Item {...PLATFORM_FORM_ACTION_LAYOUT}>
      <Space>
        <RevxButton type="primary" htmlType="submit" brandPurple>
          {t('common.save')}
        </RevxButton>
        <RevxButton onClick={onClose}>{t('common.cancel')}</RevxButton>
        {item?.id && (
          <>
            <Popconfirm
              title={t('common.confirmQuestion')}
              icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
              onConfirm={onRemove}
              okText={t('common.ok')}
              cancelText={t('common.cancel')}
            >
              <RevxButton danger>{t('common.remove')}</RevxButton>
            </Popconfirm>
          </>
        )}
      </Space>
    </Form.Item>
  );

  const disabledDate: (current: Moment) => boolean = (current) => {
    // Can not select days before today
    return current < moment().startOf('day');
  };

  return (
    <Drawer
      destroyOnClose
      title={
        item?.id ? t('platform.tf.labels.edit') : t('platform.tf.labels.create')
      }
      placement="right"
      width={DEFAULT_DRAWER_WIDTH}
      closable={true}
      onClose={onClose}
      visible={Boolean(item)}
    >
      <RevxSpin spinning={saving}>
        <Form
          {...PLATFORM_FORM_ITEM_LAYOUT}
          layout={'horizontal'}
          form={form}
          initialValues={{
            ...item,
            dateRange: [
              moment(isEmpty(item?.startDate) ? undefined : item?.startDate),
              moment(isEmpty(item?.endDate) ? undefined : item?.endDate),
            ],
          }}
          onFinish={onFinish}
        >
          <Form.Item
            label={t('common.active')}
            name="active"
            valuePropName="checked"
          >
            <Switch disabled={!Boolean(item?.id)} />
          </Form.Item>

          <Form.Item
            label={t('common.name')}
            name="name"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.tf.columns.shiftsPerDay')}
            name="shiftsPerDay"
          >
            <InputNumber min={1} />
          </Form.Item>

          <Form.Item
            label={t('platform.tf.columns.hoursPerShift')}
            name="hoursPerShift"
          >
            <InputNumber min={1} />
          </Form.Item>

          <Form.Item
            label={t('platform.tf.columns.utilizationPct')}
            name={'utilizationPct'}
          >
            <InputNumber<number>
              min={1}
              max={100}
              formatter={(v) => `${v}%`}
              parser={(v) => Number(v?.replace('%', ''))}
            ></InputNumber>
          </Form.Item>

          <Form.Item
            label={t('platform.tf.columns.daysOfWeek')}
            name={'daysOfWeek'}
            rules={[{ required: true }]}
          >
            <Select
              getPopupContainer={(e) => {
                return e as HTMLElement;
              }}
              mode="multiple"
            >
              {DaysOfWeekOptions.map((o) => (
                <Select.Option value={o.value} key={o.value}>
                  {o.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label={t('platform.tf.columns.roundUp')} name={'roundup'}>
            <Select
              getPopupContainer={(e) => {
                return e as HTMLElement;
              }}
            >
              <Select.Option value={RoundUpEnum.TO_COMPLETE_DAY}>
                {t('platform.tf.labels.toCompleteDay')}
              </Select.Option>
              <Select.Option value={RoundUpEnum.TO_NEXT_HALF_DAY}>
                {t('platform.tf.labels.toNextHalfDay')}
              </Select.Option>
              <Select.Option value={RoundUpEnum.NO_ROUNDING}>
                {t('platform.tf.labels.noRounding')}
              </Select.Option>
              <Select.Option value={RoundUpEnum.ADD_A_DAY}>
                {t('platform.tf.labels.addDay')}
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label={t('common.type')}>
            <Radio.Group
              value={String(isOngoing)}
              onChange={(e) => setIsOngoing(e.target.value === 'true')}
            >
              <Radio value="true">{t('platform.tf.labels.ongoing')}</Radio>
              <Radio value="false">
                {t('platform.tf.labels.specificDates')}
              </Radio>
            </Radio.Group>
          </Form.Item>

          {!isOngoing && (
            <>
              <Form.Item
                label={t('platform.markup.labels.dateRange')}
                name="dateRange"
                rules={[{ required: true }]}
              >
                <DatePicker.RangePicker disabledDate={disabledDate} />
              </Form.Item>
            </>
          )}
          {actionBar}
        </Form>
      </RevxSpin>
    </Drawer>
  );
};

export default TfItemForm;
